import React, { FunctionComponent, useState } from "react";
import { useNavigate } from "react-router";
import { Table } from "react-bootstrap";
import * as _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortAlphaDown, faSortAlphaUp } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";

import ExportAsCsvButton from "./ExportAsCsvButton";
import { EditIcon, SearchIcon, TrashIcon } from "../common/sidebar/icons";
import { RegistrationDetails } from "./RegistrationDetails";
import { OfferFilter } from "./OfferFilter";
import { joyColorClass } from "../common/joyColor";
import { useRegistrations } from "../../hooks/useRegistrations";
import { useSchemaProperties } from "../../hooks/useSchemaProperties";
import { useFormSchema } from "../../hooks/useFormSchema";
import { useLimits } from "../../hooks/useLimits";
import FirebaseService from "../../actions/firebase/FirebaseActions";
import Registration, { UpdateRegistration } from "../../models/registration/Registration";
import { useAuthentication } from "../../hooks/useAuthentication";
import useSubscriptionType from "../../hooks/useSubscriptionType";
import { ContentLayout } from "../common/Layout";
import "dayjs/locale/de";
import { getExportRegistrations, getFormattedRegistrations } from "../../helper/getFormattedRegistrations";
import { trackRegistrationEvent, trackFeatureUsage } from "../../helper/analytics";
import { useUserIdParam } from "../../hooks/useUserIdParam";
import "./Registrations.scss";

dayjs.locale("de");

interface Props {
  isAuthenticated: boolean;
}

type SortDirection = "ASCENDING" | "DESCENDING" | "UNSORTED";

const Registrations: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const [sortProperty, setSortProperty] = useState("");
  const [sortDirection, setSortDirection] = useState<SortDirection>("UNSORTED");
  const [buttonsDisabled, disableButtons] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [offerFilter, setOfferFilter] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAllColumns, setShowAllColumns] = useState(false);

  const userId = useUserIdParam();
  const navigate = useNavigate();
  useAuthentication(isAuthenticated);

  const [registrations, registrationsLoading, registrationsError] = useRegistrations(userId);
  const [limits, limitsLoading, limitsError] = useLimits(userId);
  const [schemaProperties, schemaPropertiesLoading, schemaPropertiesError] = useSchemaProperties(userId);
  const [formSchema] = useFormSchema(userId);
  const subscriptionType = useSubscriptionType(userId);

  const isLoading = registrationsLoading || schemaPropertiesLoading || limitsLoading;
  const isError = registrationsError || schemaPropertiesError || limitsError;

  const renderSortIcon = (property: string): React.ReactNode => {
    if (!property || sortProperty !== property || sortDirection === "UNSORTED") {
      return null;
    }
    return (
      <FontAwesomeIcon
        style={{ marginLeft: 6, color: "var(--accent)" }}
        icon={sortDirection === "ASCENDING" ? faSortAlphaDown : faSortAlphaUp}
      />
    );
  };

  const handleHeaderClick = (property: string): void => {
    setSortProperty(property);
    if (property === sortProperty) {
      if (sortDirection === "ASCENDING") {
        setSortDirection("DESCENDING");
      } else if (sortDirection === "DESCENDING") {
        setSortDirection("UNSORTED");
      } else {
        setSortDirection("ASCENDING");
      }
    } else {
      setSortDirection("ASCENDING");
    }
  };

  const sortRegistrations = (sortedRegistrations: Registration[]): void => {
    if (sortProperty !== "") {
      if (sortDirection === "ASCENDING") {
        sortedRegistrations.sort((a, b) => {
          if (a.formData[sortProperty] < b.formData[sortProperty]) return -1;
          if (a.formData[sortProperty] > b.formData[sortProperty]) return 1;
          return 0;
        });
      } else if (sortDirection === "DESCENDING") {
        sortedRegistrations.sort((a, b) => {
          if (a.formData[sortProperty] > b.formData[sortProperty]) return -1;
          if (a.formData[sortProperty] < b.formData[sortProperty]) return 1;
          return 0;
        });
      } else {
        setSortProperty("");
      }
    }
  };

  const handleDelete = (registrationId: string): void => {
    if (!window.confirm("Anmeldung wirklich löschen?")) {
      trackRegistrationEvent("delete_cancelled", { registration_id: registrationId });
      return;
    }
    disableButtons(true);
    trackRegistrationEvent("delete_initiated", { registration_id: registrationId });

    FirebaseService.deleteRegistration(userId, registrationId)
      .then(() => {
        trackRegistrationEvent("delete_completed", { registration_id: registrationId });
        setSelectedId((current) => (current === registrationId ? null : current));
      })
      .catch(() => {
        trackRegistrationEvent("delete_failed", { registration_id: registrationId });
        toast.error("Die Anmeldung konnte nicht gelöscht werden. Bitte versuchen Sie es später nochmals.");
      })
      .finally(() => disableButtons(false));
  };

  const saveRegistration = async (registrationId: string, formData: Record<string, unknown>): Promise<void> => {
    const update: UpdateRegistration = { id: registrationId, formData };
    try {
      await FirebaseService.updateRegistration(userId, update);
      toast.success("Die Anmeldung wurde gespeichert.");
    } catch {
      toast.error("Die Anmeldung konnte nicht gespeichert werden. Bitte versuchen Sie es später nochmals.");
    } finally {
      trackRegistrationEvent("inline_edit_saved", { registration_id: registrationId });
    }
  };

  const openEditor = (registrationId: string): void => {
    trackFeatureUsage("edit_registration", { registration_id: registrationId });
    navigate(`/user/${userId}/registrations/${registrationId}`);
  };

  const handleDeleteAll = (): void => {
    disableButtons(true);
    if (
      window.confirm(
        "Wir empfehlen Ihnen, vor dem Löschen alle Anmeldungen zu exportieren. " +
          "Wenn Sie fortfahren werden alle Anmeldungen unwiderruflich gelöscht. " +
          "Möchten Sie fortfahren?"
      )
    ) {
      const registrationCount = registrations?.length || 0;
      trackRegistrationEvent("bulk_delete_initiated", { count: registrationCount });

      FirebaseService.deleteAllRegistrations(userId)
        .then(() => {
          trackRegistrationEvent("bulk_delete_completed", { count: registrationCount });
          toast.success("Alle Anmeldungen wurden erfolgreich gelöscht");
        })
        .catch(() => {
          trackRegistrationEvent("bulk_delete_failed", { count: registrationCount });
          toast.error("Fehler beim Löschen der Anmeldungen aufgetreten. Bitte versuchen Sie es später erneut.");
        })
        .finally(() => disableButtons(false));
    } else {
      trackRegistrationEvent("bulk_delete_cancelled");
      disableButtons(false);
    }
  };

  if (isError) return <p>Fehler beim Laden der Daten</p>;

  const limitProperties = limits?.map((limit) => limit.property);
  const sortedRegistrations = _.cloneDeep(registrations);

  sortRegistrations(sortedRegistrations);
  const formattedRegistrations = getFormattedRegistrations(sortedRegistrations);
  const registrationsForExport = getExportRegistrations(formattedRegistrations, limits);

  const visibleProperties = showAllColumns ? schemaProperties : schemaProperties.slice(0, 6);
  const hasHiddenColumns = schemaProperties.length > visibleProperties.length;

  const term = search.trim().toLowerCase();
  const visibleRegistrations = formattedRegistrations
    .filter(
      (registration) => offerFilter.length === 0 || offerFilter.some((property) => registration.formData[property])
    )
    .filter(
      (registration) =>
        !term ||
        Object.values(registration.formData).some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(term)
        )
    );

  const selectedRegistration = registrations.find((registration) => registration.id === selectedId);

  return (
    <ContentLayout
      isLoading={isLoading}
      title={"Anmeldungen"}
      subtitle={`${registrations.length} Einträge`}
      siteTitle={"Spielgruppe Online - Anmeldungen"}
      actions={
        <>
          <label className="header-search">
            <SearchIcon style={{ color: "var(--text-faint)" }} />
            <input
              value={search}
              placeholder="Suchen …"
              aria-label="Anmeldungen durchsuchen"
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          {limits.length > 0 && (
            <OfferFilter
              limits={limits}
              registrations={registrations}
              selected={offerFilter}
              onChange={setOfferFilter}
            />
          )}
          {(hasHiddenColumns || showAllColumns) && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowAllColumns(!showAllColumns)}>
              {showAllColumns ? "Weniger Spalten" : "Alle Spalten"}
            </button>
          )}
          <ExportAsCsvButton
            properties={schemaProperties}
            registrations={registrationsForExport}
            limits={limits}
            subscriptionType={subscriptionType}
          />
          <button type="button" className="btn btn-secondary" disabled={buttonsDisabled} onClick={handleDeleteAll}>
            Alle löschen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={buttonsDisabled}
            onClick={() => {
              trackFeatureUsage("create_registration_button");
              navigate(`/user/${userId}/registrations/create`);
            }}>
            Neue Anmeldung
          </button>
        </>
      }>
      <div className={`split-view${selectedRegistration ? " has-detail" : ""}`}>
        <section className="panel">
          <div className="registrations-table-wrapper">
            <Table hover className={"registrations-table sticky-table"}>
              <thead>
                <tr>
                  {visibleProperties.map((property) => (
                    <th key={property} onClick={() => handleHeaderClick(property)}>
                      {property} {renderSortIcon(property)}
                    </th>
                  ))}
                  <th>Angebot</th>
                  <th>Preis</th>
                  <th onClick={() => setSortProperty("")}>Zeitpunkt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleRegistrations.map((registration) => (
                  <tr
                    key={registration.id}
                    className={registration.id === selectedId ? "is-selected" : undefined}
                    onClick={() => setSelectedId(registration.id)}>
                    {visibleProperties.map((property) => (
                      <td key={property}>{registration.formData[property]}</td>
                    ))}
                    <td>
                      {limitProperties
                        ?.filter((property) => registration.formData[property])
                        .map((property) => (
                          <span key={property} className={`tag-pill tag-pill${joyColorClass(property)} me-1`}>
                            {property}
                          </span>
                        ))}
                    </td>
                    <td>
                      CHF{" "}
                      {limits
                        .map((limit) => (registration.formData[limit.property] && limit.price ? limit.price : 0))
                        .reduce((total, currentValue) => total + currentValue, 0)}
                    </td>
                    <td>{dayjs(registration.creation.createdAt).format("DD.MM.YYYY HH:mm")}</td>
                    <td>
                      <div className="data-row__actions">
                        <button
                          type="button"
                          title="Bearbeiten"
                          className="btn btn-icon"
                          disabled={buttonsDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            openEditor(registration.id);
                          }}>
                          <EditIcon />
                        </button>
                        <button
                          type="button"
                          title="Löschen"
                          className="btn btn-icon btn-icon-danger"
                          disabled={buttonsDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(registration.id);
                          }}>
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="panel__bar">
            <span>
              {visibleRegistrations.length} von {formattedRegistrations.length} Anmeldungen
              {hasHiddenColumns && ` · ${schemaProperties.length - visibleProperties.length} Spalten ausgeblendet`}
            </span>
          </div>
        </section>

        {selectedRegistration && (
          <RegistrationDetails
            registration={selectedRegistration}
            properties={schemaProperties}
            limits={limits}
            formSchema={formSchema}
            onClose={() => setSelectedId(null)}
            onOpenEditor={() => openEditor(selectedRegistration.id)}
            onDelete={() => handleDelete(selectedRegistration.id)}
            onSave={(formData) => saveRegistration(selectedRegistration.id, formData)}
          />
        )}
      </div>
    </ContentLayout>
  );
};

export default Registrations;
